import { useState } from "react";
import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { db, storage } from "../../lib/firebase";
import styles from "./ImageUploader.module.css";

/**
 * Subida genérica de imagen: Storage → Firestore.
 *
 * Props:
 *  - collectionPath: ruta Firestore donde se registra el doc (ej. "heroImages")
 *  - storageFolder:  carpeta Storage donde sube el archivo (ej. "hero")
 *  - existing:       array de items ya existentes [{id, url, storagePath, alt}]
 *  - onChange(item?, removedId?): callback al subir/eliminar
 */
export default function ImageUploader({
  collectionPath,
  storageFolder,
  existing = [],
  onChange,
}) {
  const [progress, setProgress] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  function uploadResumable(storagePath, file) {
    const sRef = ref(storage, storagePath);
    const task = uploadBytesResumable(sRef, file);
    return new Promise((resolve, reject) => {
      task.on(
        "state_changed",
        (s) =>
          setProgress(
            Math.round((s.bytesTransferred / s.totalBytes) * 100)
          ),
        (err) => reject(err),
        async () => {
          try {
            const url = await getDownloadURL(task.snapshot.ref);
            resolve({ url, storagePath });
          } catch (err) {
            reject(err);
          }
        }
      );
    });
  }

  async function handleFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("Solo se permiten imágenes.");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError("Imagen demasiado grande (máx 10 MB).");
      return;
    }

    setError(null);
    setBusy(true);
    setProgress(0);

    try {
      const safeName = `${Date.now()}-${file.name.replace(/\s+/g, "_")}`;
      const storagePath = `${storageFolder}/${safeName}`;
      const { url } = await uploadResumable(storagePath, file);

      const docRef = await addDoc(collection(db, collectionPath), {
        url,
        storagePath,
        alt: file.name,
        order: existing.length,
        active: true,
        createdAt: serverTimestamp(),
      });

      onChange?.({ id: docRef.id, url, storagePath, alt: file.name });
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
      setProgress(0);
      e.target.value = "";
    }
  }

  async function handleDelete(item) {
    if (!confirm("¿Eliminar imagen?")) return;
    setBusy(true);
    try {
      try {
        await deleteObject(ref(storage, item.storagePath));
      } catch {
        /* el archivo puede no existir ya */
      }
      await deleteDoc(doc(db, collectionPath, item.id));
      onChange?.(null, item.id);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function handleReplace(item, file) {
    if (!file?.type?.startsWith("image/")) return;
    setBusy(true);
    setProgress(0);
    try {
      try {
        await deleteObject(ref(storage, item.storagePath));
      } catch {
        /* ignorar */
      }
      const safeName = `${Date.now()}-${file.name.replace(/\s+/g, "_")}`;
      const storagePath = `${storageFolder}/${safeName}`;
      const { url } = await uploadResumable(storagePath, file);

      await updateDoc(doc(db, collectionPath, item.id), {
        url,
        storagePath,
        alt: file.name,
        updatedAt: serverTimestamp(),
      });
      onChange?.({ id: item.id, url, storagePath, alt: file.name });
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
      setProgress(0);
    }
  }

  async function toggleActive(item) {
    await updateDoc(doc(db, collectionPath, item.id), {
      active: !item.active,
    });
    onChange?.({ ...item, active: !item.active });
  }

  async function handleRename(item, value) {
    const alt = value.trim();
    if (!alt || alt === item.alt) return;
    try {
      await updateDoc(doc(db, collectionPath, item.id), { alt });
      onChange?.({ ...item, alt });
    } catch (err) {
      setError(err.message);
    }
  }

  return (
    <div className={styles.wrap}>
      <label className={`${styles.dropzone} ${busy ? styles.busy : ""}`}>
        <input
          type="file"
          accept="image/*"
          onChange={handleFile}
          disabled={busy}
          hidden
        />
        <span className="mono-tag">
          {busy
            ? `↳ subiendo · ${progress}%`
            : "// arrastra o haz clic para subir imagen"}
        </span>
        <span className={styles.hint}>PNG, JPG o WebP · ≤ 10 MB</span>
      </label>

      {error && <p className={styles.error}>// {error}</p>}

      <ul className={styles.grid}>
        {existing.map((item) => (
          <li
            key={item.id}
            className={`${styles.card} ${item.active === false ? styles.inactive : ""}`}
          >
            <img src={item.url} alt={item.alt} />
            <div className={styles.cardMeta}>
              <input
                className={styles.name}
                defaultValue={item.alt || ""}
                title="Editar nombre"
                aria-label="Nombre de la imagen"
                onBlur={(e) => handleRename(item, e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") e.currentTarget.blur();
                }}
              />
              {"active" in item && (
                <button
                  type="button"
                  onClick={() => toggleActive(item)}
                  className={styles.toggle}
                  title="Mostrar/ocultar en la web"
                >
                  {item.active ? "activa" : "oculta"}
                </button>
              )}
            </div>
            <div className={styles.actions}>
              <label className={styles.replace}>
                Reemplazar
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={(e) =>
                    e.target.files?.[0] && handleReplace(item, e.target.files[0])
                  }
                />
              </label>
              <button
                type="button"
                onClick={() => handleDelete(item)}
                className={styles.delete}
              >
                Eliminar
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
